import {I18n} from '@aws-amplify/core';
import {DataTableColumn} from 'mantine-datatable';
import {NavLink} from 'react-router-dom';

import {StatusBadge} from '../profile/StatusBadge';
import {formatTimestamp} from '../../../utils/TableTimestamps';
import {SetupIntent} from '../types';

// FNP-918: was `tables/columns.tsx` — a bare `getColumns()` array where EVERY
// column was `render`-only, with no `accessor` on any of them.
//
// That is the distinction that made this module unsortable. `render` controls
// how a cell LOOKS; `accessor` is what sorting sorts BY. A render-only config
// looks complete in review and makes sorting structurally impossible (§3z).
//
// ⚠️ EVERY COLUMN BELOW RENDERS A FIELD THE LIST QUERY ACTUALLY SELECTS.
// `listSetupIntents` selects id, clientSecret, created, customerId, description,
// lastSetupError, paymentMethod, paymentMethodTypes, status and usage — checked
// against services/queries.ts. A column whose field is not in the selection set
// renders an em-dash on every row while looking correct in code review.
//
// ⚠️ `clientSecret` IS SELECTED BY THE QUERY BUT IS DELIBERATELY NOT A COLUMN.
// It is the credential that authorises confirming this setup intent from a
// browser. It is not rendered, not sorted and not searched (§11).
export const useSetupIntentsTableColumns = () => {

    const columns: DataTableColumn<SetupIntent>[] = [
        {
            // Sorts by DESCRIPTION, which is what the cell leads with. The
            // render falls back to the id so a row is never blank, but sorting
            // on a coalesced value would interleave two different namespaces —
            // descriptions and `seti_...` ids — into one meaningless order.
            accessor: 'description',
            sortable: true,
            title: I18n.get('Description'),
            width: '1fr',
            render: (item: SetupIntent) => item.description || item.id,
        },
        {
            accessor: 'customerId',
            sortable: true,
            title: I18n.get('Customer'),
            width: '1fr',
            // Nullable fields render the em-dash placeholder, never blank or
            // `undefined` (§13). The old config used a hyphen '-'.
            render: (item: SetupIntent) => item.customerId
                ? <NavLink to={`/customers/${item.customerId}`}>{item.customerId}</NavLink>
                : '—',
        },
        {
            accessor: 'paymentMethod',
            sortable: true,
            title: I18n.get('Payment Method'),
            width: '1fr',
            render: (item: SetupIntent) => item.paymentMethod ?? '—',
        },
        {
            accessor: 'usage',
            sortable: true,
            title: I18n.get('Usage'),
            width: '1fr',
            render: (item: SetupIntent) => item.usage ?? '—',
        },
        {
            accessor: 'status',
            sortable: true,
            title: I18n.get('Status'),
            width: '1fr',
            // Enum renders as a badge, not a raw string (§13).
            render: (item: SetupIntent) => <StatusBadge status={item.status}/>,
        },
        {
            // ⚠️ FNP-1106. Sorts AND renders on the normalised MILLISECOND
            // value, not the raw `created`.
            //
            // `created: Int` from Stripe is epoch SECONDS; `formatTimestamp`
            // hands its value to moment, which reads a number as MILLIS. A raw
            // `created` of 1_700_000_000 therefore renders as 20 Jan 1970 —
            // plausible enough to pass review and wrong on every row. The
            // normalisation happens once in useSetupIntents so display and sort
            // agree; `created` (seconds) remains the source of truth.
            accessor: 'createdAtMillis',
            sortable: true,
            title: I18n.get('Created'),
            width: '1fr',
            render: (item: SetupIntent) => formatTimestamp(item.createdAtMillis),
        },
    ];

    return {columns};
};
